import ApiService from "@/services/api.service";

// ACTIONS
export const FETCH_PERIODI = "FETCH_PERIODI";
export const FETCH_RIGHE_PERIODO = "FETCH_RIGHE_PERIODO";
export const FETCH_CATALOGO_SERVIZI = "FETCH_CATALOGO_SERVIZI";
export const FETCH_LISTINO_BASE = "FETCH_LISTINO_BASE";
export const FETCH_LISTINO_CLIENTE = "FETCH_LISTINO_CLIENTE";

// MUTATIONS
export const SET_PERIODI = "SET_PERIODI";
export const SET_RIGHE = "SET_RIGHE";
export const SET_CATALOGO_SERVIZI = "SET_CATALOGO_SERVIZI";
export const SET_LISTINO_BASE = "SET_LISTINO_BASE";
export const SET_LISTINO_CLIENTE = "SET_LISTINO_CLIENTE";
export const SET_LOADING = "SET_LOADING";
export const SET_ERRORE_CARICAMENTO = "SET_ERRORE_CARICAMENTO";
export const SET_ERRORE_SUPPORTO = "SET_ERRORE_SUPPORTO";

// Controller di backend: [Route("api/[controller]")].
// La baseURL di axios è VUE_APP_ROOT_API (default "/api").
//
// Fase 4m — pagina "I miei servizi" (usage del cliente). Nessuno di questi
// endpoint è nuovo: sono le stesse letture usate dalla pagina "Servizi attivi
// del cliente" (Fase 4b/4i/4l), richiamate qui per un modulo store dedicato
// così da non toccare le azioni già presenti nel modulo "listini".
//
// Scoping lato backend (Fase 4l, FatturazioneRigheController.Read/Periodi):
//   - `GET /api/FatturazioneRighe/periodi` per un utente NON superadmin elenca
//     solo i periodi che hanno righe del suo cliente;
//   - `GET /api/FatturazioneRighe` per un utente NON superadmin ignora/rifiuta
//     `idCliente` e legge il proprio cliente: il parametro va inviato SOLO da
//     un superadmin.
const RESOURCE_FATTURAZIONE = "FatturazioneRighe";
const RESOURCE_SERVIZI = "Servizio";
const RESOURCE_LISTINO_BASE = "Listino";
const RESOURCE_LISTINO_CLIENTI = "ListinoClienti";

// Righe della riga cliente (listini_clienti) lette in una sola richiesta:
// servono a risolvere la quota inclusa di primo livello (riga del cliente) e
// sono poche per cliente. `GET /api/ListinoClienti` è paginata e per un utente
// cliente il backend vi aggiunge da sé il filtro sul proprio idCliente.
const PAGE_SIZE_LISTINO_CLIENTE = 200;

// Testo usato quando l'errore non contiene un messaggio leggibile.
const MESSAGGIO_ERRORE_GENERICO =
    "Si è verificato un errore, riprova più tardi.";

// Il backend risponde agli errori con HTTP 500 e body {Status, Message}
// (Response<object> di questa codebase). A seconda della configurazione JSON il
// campo arriva come "message" o "Message": si accettano entrambe le forme, più le
// varianti ProblemDetails, e in ogni caso la mutation riceve una stringa.
const toMessage = (err) => {
    if (typeof err === "string") {
        return err.trim() || MESSAGGIO_ERRORE_GENERICO;
    }
    if (!err) {
        return MESSAGGIO_ERRORE_GENERICO;
    }

    const candidati = [
        err.response?.data?.message,
        err.response?.data?.Message,
        err.response?.data?.detail,
        err.response?.data?.title,
        err.response?.data,
        err.data?.message,
        err.data?.Message,
        err.detail,
        err.title,
        err.message,
    ];
    const messaggio = candidati.find(
        (valore) => typeof valore === "string" && valore.trim() !== ""
    );
    if (messaggio) {
        return messaggio.trim();
    }

    try {
        const serializzato = JSON.stringify(err);
        if (serializzato && serializzato !== "{}" && serializzato !== "[]") {
            return serializzato;
        }
    } catch {
        // Oggetto non serializzabile: resta il messaggio generico.
    }

    return MESSAGGIO_ERRORE_GENERICO;
};

// La lettura paginata di /api/ListinoClienti risponde {Items, TotalCount}
// (PagedResults<ListinoClientiDto>): l'array delle righe è accettato anche in
// forma piatta, senza dare per scontata una delle due forme.
const estraiRighe = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.items)) return data.items;
    if (data && Array.isArray(data.Items)) return data.Items;
    return [];
};

const state = {
    // Periodi elaborati: {year, month, numeroRighe, calcolatoIlMax}, dal più
    // recente (il backend li ordina così).
    periodi: [],
    // Righe dello snapshot di fatturazione del periodo selezionato
    // (FatturazioneRigaDto).
    righe: [],
    // Dati di supporto: catalogo servizi (unità di misura, quota di catalogo,
    // contatore chiave) e listino (descrizione, unità incluse, frequenza).
    catalogoServizi: [],
    listinoBase: [],
    listinoCliente: [],
    loading: false,
    // Errore della lettura principale (periodi e righe): stringa o null.
    erroreCaricamento: null,
    // Errore delle letture di supporto (catalogo/listino): stringa o null.
    // Tenuto separato: senza supporto la pagina mostra comunque consumi e
    // importi, quindi non è un errore bloccante.
    erroreSupporto: null,
};

const getters = {
    getPeriodi: (state) => state.periodi,
    getRighe: (state) => state.righe,
    getCatalogoServizi: (state) => state.catalogoServizi,
    getListinoBase: (state) => state.listinoBase,
    getListinoCliente: (state) => state.listinoCliente,
    getLoading: (state) => state.loading,
    getErroreCaricamento: (state) => state.erroreCaricamento,
    getErroreSupporto: (state) => state.erroreSupporto,
};

const actions = {
    // GET /api/FatturazioneRighe/periodi — periodi elaborati disponibili.
    [FETCH_PERIODI](context) {
        context.commit(SET_LOADING, true);
        context.commit(SET_ERRORE_CARICAMENTO, null);

        return new Promise((resolve) => {
            ApiService.get(`${RESOURCE_FATTURAZIONE}/periodi`)
                .then(({ data }) => {
                    context.commit(SET_PERIODI, data);
                    resolve(data);
                })
                .catch((error) => {
                    // Svuota l'elenco: senza questo il selettore mostrerebbe i
                    // periodi della lettura precedente mentre l'alert dice che
                    // la lettura è fallita.
                    context.commit(SET_PERIODI, []);
                    context.commit(
                        SET_ERRORE_CARICAMENTO,
                        toMessage(error)
                    );
                    resolve([]);
                })
                .finally(() => context.commit(SET_LOADING, false));
        });
    },

    // GET /api/FatturazioneRighe?anno=&mese=&idCliente=
    // anno e mese sono obbligatori. `idCliente` è inviato SOLO da un
    // superadmin: per un utente cliente il backend legge il proprio cliente e
    // rifiuta il filtro su un altro.
    [FETCH_RIGHE_PERIODO](context, { anno, mese, idCliente }) {
        context.commit(SET_LOADING, true);
        context.commit(SET_ERRORE_CARICAMENTO, null);

        const parametri = [
            `anno=${encodeURIComponent(anno)}`,
            `mese=${encodeURIComponent(mese)}`,
        ];
        if (idCliente !== null && idCliente !== undefined && idCliente !== "") {
            parametri.push(`idCliente=${encodeURIComponent(idCliente)}`);
        }

        return new Promise((resolve) => {
            ApiService.get(`${RESOURCE_FATTURAZIONE}?${parametri.join("&")}`)
                .then(({ data }) => {
                    context.commit(SET_RIGHE, data);
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_RIGHE, []);
                    context.commit(
                        SET_ERRORE_CARICAMENTO,
                        toMessage(error)
                    );
                    resolve([]);
                })
                .finally(() => context.commit(SET_LOADING, false));
        });
    },

    // GET /api/Servizio — catalogo servizi: unità di misura, quota inclusa di
    // catalogo e contatore chiave.
    [FETCH_CATALOGO_SERVIZI](context) {
        return new Promise((resolve) => {
            ApiService.get(RESOURCE_SERVIZI)
                .then(({ data }) => {
                    context.commit(SET_CATALOGO_SERVIZI, data);
                    context.commit(SET_ERRORE_SUPPORTO, null);
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_CATALOGO_SERVIZI, []);
                    context.commit(
                        SET_ERRORE_SUPPORTO,
                        toMessage(error)
                    );
                    resolve([]);
                });
        });
    },

    // GET /api/Listino — listino base, senza parametri e senza paginazione:
    // serve per descrizione, frequenza, unità incluse e servizio collegato.
    [FETCH_LISTINO_BASE](context) {
        return new Promise((resolve) => {
            ApiService.get(RESOURCE_LISTINO_BASE)
                .then(({ data }) => {
                    context.commit(SET_LISTINO_BASE, data);
                    context.commit(SET_ERRORE_SUPPORTO, null);
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_LISTINO_BASE, []);
                    context.commit(
                        SET_ERRORE_SUPPORTO,
                        toMessage(error)
                    );
                    resolve([]);
                });
        });
    },

    // GET /api/ListinoClienti — righe del cliente: quota inclusa di primo
    // livello (unitaincluse) per le voci attive.
    // `idCliente` nella mini-lingua del parametro `search` è inviato SOLO da un
    // superadmin: per gli altri utenti il backend aggiunge da sé il proprio
    // cliente e va in errore se il filtro arriva dalla richiesta.
    [FETCH_LISTINO_CLIENTE](context, { idCliente }) {
        const parametri = [
            `pageIndex=${encodeURIComponent(0)}`,
            `pageSize=${encodeURIComponent(PAGE_SIZE_LISTINO_CLIENTE)}`,
        ];
        if (idCliente !== null && idCliente !== undefined && idCliente !== "") {
            parametri.push(
                `search=${encodeURIComponent(`idCliente:${idCliente}`)}`
            );
        }

        return new Promise((resolve) => {
            ApiService.get(
                `${RESOURCE_LISTINO_CLIENTI}?${parametri.join("&")}`
            )
                .then(({ data }) => {
                    context.commit(SET_LISTINO_CLIENTE, estraiRighe(data));
                    context.commit(SET_ERRORE_SUPPORTO, null);
                    resolve(estraiRighe(data));
                })
                .catch((error) => {
                    context.commit(SET_LISTINO_CLIENTE, []);
                    context.commit(
                        SET_ERRORE_SUPPORTO,
                        toMessage(error)
                    );
                    resolve([]);
                });
        });
    },
};

const mutations = {
    [SET_PERIODI](state, list) {
        state.periodi = Array.isArray(list) ? list : [];
    },
    [SET_RIGHE](state, list) {
        state.righe = Array.isArray(list) ? list : [];
    },
    [SET_CATALOGO_SERVIZI](state, list) {
        state.catalogoServizi = Array.isArray(list) ? list : [];
    },
    [SET_LISTINO_BASE](state, list) {
        state.listinoBase = Array.isArray(list) ? list : [];
    },
    [SET_LISTINO_CLIENTE](state, list) {
        state.listinoCliente = Array.isArray(list) ? list : [];
    },
    [SET_LOADING](state, val) {
        state.loading = val;
    },
    // Sempre null oppure stringa: distingue i dati vuoti dal caricamento fallito.
    [SET_ERRORE_CARICAMENTO](state, valore) {
        state.erroreCaricamento =
            typeof valore === "string" && valore !== "" ? valore : null;
    },
    [SET_ERRORE_SUPPORTO](state, valore) {
        state.erroreSupporto =
            typeof valore === "string" && valore !== "" ? valore : null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
